import { ChangeEvent } from "react";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import AmountInput from "../../shared/AmountInput";
import { FieldLabel, SectionTitle, inputSx } from "../../shared/FormField";
import { PRICE_BOOK_TYPE_OPTIONS } from "../constants";
import {
  PriceBookDrawerMode,
  PriceBookFormErrors,
  PriceBookFormState,
} from "../types";

/** A code the user cannot type is shown as a filled grey field rather than plain text. */
const readOnlyFieldSx = {
  ...inputSx,
  "& .MuiInputBase-root.Mui-disabled": { bgcolor: "background.default" },
};

interface GeneralInfoFieldsProps {
  mode: PriceBookDrawerMode;
  form: PriceBookFormState;
  errors: PriceBookFormErrors;
  disabled: boolean;
  onChangeName: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeType: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeDiscountPercent: (discountPercent: string) => void;
}

/** A % keeps 2 decimals, like the API's DECIMAL(5,2). */
const PERCENT_FRACTION_DIGITS = 2;

/**
 * The pricebook's own fields. The code is never typed — the API hands out every one — so
 * it is only worth showing for a stored pricebook, where it identifies the record being
 * edited; a create and a copy are issued theirs and have nothing to read back yet. Name
 * and type are editable in every mode because the duplicate endpoint honours them. The
 * book % is set here only for a new book or a copy; a stored book's % is edited on its
 * detail page, next to the prices it moves.
 */
export default function GeneralInfoFields({
  mode,
  form,
  errors,
  disabled,
  onChangeName,
  onChangeType,
  onChangeDiscountPercent,
}: GeneralInfoFieldsProps) {
  const showsStoredCode = mode === "edit";
  const showsDiscountPercent = mode !== "edit";

  return (
    <>
      <SectionTitle>ข้อมูลทั่วไป</SectionTitle>

      {showsStoredCode && (
        <>
          <FieldLabel>รหัสสมุดราคา</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.code}
            disabled
            helperText="แก้ไขไม่ได้"
            sx={readOnlyFieldSx}
          />
        </>
      )}

      <FieldLabel required>ชื่อสมุดราคา</FieldLabel>
      <TextField
        size="small"
        fullWidth
        value={form.name}
        onChange={onChangeName}
        placeholder="กรอกชื่อสมุดราคา"
        disabled={disabled}
        error={Boolean(errors.name)}
        helperText={errors.name}
        sx={inputSx}
      />

      <FieldLabel>ประเภทสมุดราคา</FieldLabel>
      <TextField
        select
        size="small"
        fullWidth
        value={form.type ?? ""}
        onChange={onChangeType}
        disabled={disabled}
        sx={inputSx}
      >
        {PRICE_BOOK_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {showsDiscountPercent && (
        <>
          <FieldLabel required>ส่วนลดทั้งสมุด (%)</FieldLabel>
          <Box sx={inputSx}>
            <AmountInput
              value={form.discountPercent}
              maxFractionDigits={PERCENT_FRACTION_DIGITS}
              disabled={disabled}
              error={errors.discountPercent}
              ariaLabel="ส่วนลดทั้งสมุด %"
              onChange={onChangeDiscountPercent}
            />
          </Box>
        </>
      )}
    </>
  );
}
